import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebProfitSpinner from "../components/system/WebProfitSpinner";
import { acquisitionApi } from "../api/acquisition";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectsContext";
import {
  acquisitionCampaign,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  clearAcquisitionDraft,
  loadAcquisitionDraft,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";

// BLOCK 3 · Full-screen acquisition/onboarding funnel + pre-registration audience classification.
// STATUS: STATIC_PASS. Runtime/mobile keyboard verification pending staging deploy.
// UX CONTRACT: one decision per screen, no page scrollbar, progress always visible.
// RESILIENCE: every answer is persisted locally first; analytics/API failures never erase progress.

const BRAND = { orange: "#F35A3A", dark: "#0B1016", panel: "#111820", muted: "rgba(255,255,255,.58)" };
const STEPS = ["intro", "business", "system", "volume", "need", "plan", "business_identity", "contact"];
const PROFILE_KEYS = new Set(["commerce", "services", "gym", "restaurant", "real_estate"]);

const BUSINESS_OPTIONS = [
  ["commerce", "Comercio"],
  ["services", "Servicios"],
  ["restaurant", "Gastronomía"],
  ["gym", "Gimnasio / academia"],
  ["real_estate", "Inmobiliaria"],
  ["other", "Otro negocio"],
];
const SYSTEM_OPTIONS = [
  ["starting", "Estoy empezando"],
  ["paper_whatsapp", "Papel / WhatsApp"],
  ["spreadsheet", "Excel / planillas"],
  ["other_system", "Otro sistema"],
  ["digital", "Ya tengo todo digital"],
];
const VOLUME_OPTIONS = [
  ["none", "No manejo productos"],
  ["1_20", "1 a 20"],
  ["21_100", "21 a 100"],
  ["101_500", "101 a 500"],
  ["501_2000", "501 a 2.000"],
  ["2000_plus", "Más de 2.000"],
];
const NEED_OPTIONS = [
  ["sell", "Vender mejor"],
  ["stock", "Ordenar stock"],
  ["cash", "Controlar caja"],
  ["customers", "Seguir clientes"],
  ["appointments", "Ordenar turnos"],
  ["website", "Mejorar mi web"],
  ["organize_all", "Ordenar todo"],
];

const TASK_LABELS = {
  import_items: "Importar tu catálogo",
  create_first_item: "Cargar tu primer producto",
  initial_stock_count: "Confirmar el stock real",
  create_first_service: "Cargar tu primer servicio",
  create_first_property: "Cargar tu primera propiedad",
  configure_business: "Configurar la base del negocio",
  register_first_sale: "Registrar tu primera venta",
  reconcile_stock: "Dejar el stock consistente",
  configure_cash: "Configurar caja y saldos",
  create_first_customer: "Crear tu primer cliente",
  configure_agenda: "Configurar tu agenda",
  publish_business_page: "Publicar tu página",
  open_daily_control: "Activar tu control diario",
};

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function localRecommendation(profile) {
  const goals = [];
  if (profile.businessProfile === "commerce") goals.push("sellProducts", "manageCustomers");
  if (profile.businessProfile === "services") goals.push("offerServices", "manageCustomers");
  if (profile.businessProfile === "gym") goals.push("offerServices", "manageCustomers", "manageMemberships", "takeReservations");
  if (profile.businessProfile === "restaurant") goals.push("gastronomicMenu", "manageCustomers", "takeReservations");
  if (profile.businessProfile === "real_estate") goals.push("manageProperties", "manageLeases", "manageLeaseCollections");
  if (profile.primaryNeed === "sell") goals.push("sellProducts");
  if (profile.primaryNeed === "customers") goals.push("manageCustomers");
  if (profile.primaryNeed === "appointments") goals.push("takeReservations");
  if (["stock", "cash", "organize_all"].includes(profile.primaryNeed)) goals.push("controlBusiness");

  const selectedGoals = unique(goals);
  const capabilities = {
    memberships: profile.businessProfile === "gym" || selectedGoals.includes("manageMemberships"),
    gastronomicMenu: profile.businessProfile === "restaurant" || selectedGoals.includes("gastronomicMenu"),
    tables: profile.businessProfile === "restaurant" && selectedGoals.includes("takeReservations"),
    tableReservations: profile.businessProfile === "restaurant" && selectedGoals.includes("takeReservations"),
    kitchenOrders: profile.businessProfile === "restaurant",
    recipes: profile.businessProfile === "restaurant",
    properties: profile.businessProfile === "real_estate" || selectedGoals.includes("manageProperties"),
    leases: profile.businessProfile === "real_estate" || selectedGoals.includes("manageLeases"),
    leaseCollections: profile.businessProfile === "real_estate" || selectedGoals.includes("manageLeaseCollections"),
  };
  const manyItems = ["101_500", "501_2000", "2000_plus"].includes(profile.itemVolumeBand);
  const migrates = ["spreadsheet", "other_system", "digital"].includes(profile.currentSystem);
  const productBusiness = ["commerce", "restaurant"].includes(profile.businessProfile) || (profile.itemVolumeBand && profile.itemVolumeBand !== "none");
  const firstTasks = [];
  if (productBusiness) {
    firstTasks.push(migrates || manyItems ? "import_items" : "create_first_item", "initial_stock_count");
  } else if (["services", "gym"].includes(profile.businessProfile)) firstTasks.push("create_first_service");
  else if (profile.businessProfile === "real_estate") firstTasks.push("create_first_property");
  else firstTasks.push("configure_business");
  const needTask = {
    sell: "register_first_sale",
    stock: "reconcile_stock",
    cash: "configure_cash",
    customers: "create_first_customer",
    appointments: "configure_agenda",
    website: "publish_business_page",
    organize_all: "open_daily_control",
  }[profile.primaryNeed] || "open_daily_control";
  firstTasks.push(needTask);
  return { routeKey: `${profile.businessProfile || "general"}:${profile.primaryNeed || "start"}`, businessGoals: selectedGoals, capabilities, firstTasks: unique(firstTasks).slice(0, 3) };
}

function OptionGrid({ options, selected, onSelect }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))" }, gap: { xs: .8, sm: 1 } }}>
      {options.map(([key, label]) => {
        const active = selected === key;
        return (
          <Button key={key} onClick={() => onSelect(key)} variant="outlined" sx={{ minHeight: { xs: 50, sm: 58 }, justifyContent: "flex-start", px: 1.7, borderRadius: 3, textTransform: "none", fontWeight: 900, color: active ? "#fff" : "rgba(255,255,255,.82)", bgcolor: active ? "rgba(243,90,58,.18)" : "rgba(255,255,255,.035)", borderColor: active ? "rgba(243,90,58,.55)" : "rgba(255,255,255,.09)", boxShadow: active ? "0 0 0 3px rgba(243,90,58,.08)" : "none", "&:hover": { borderColor: "rgba(243,90,58,.42)", bgcolor: "rgba(243,90,58,.10)", transform: "translateY(-1px)" } }}>
            {label}
          </Button>
        );
      })}
    </Box>
  );
}

export default function EmpecemosPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, register, loading: authLoading } = useAuth();
  const { createProject } = useProjects();
  const initialDraft = React.useMemo(() => loadAcquisitionDraft(), []);
  const sessionId = React.useMemo(() => initialDraft.sessionId || acquisitionSessionId(), [initialDraft.sessionId]);
  const visitorId = React.useMemo(() => initialDraft.visitorId || acquisitionVisitorId(), [initialDraft.visitorId]);
  const referralCode = String(searchParams.get("ref") || initialDraft.referralCode || "").trim().toUpperCase();
  const quickNeed = String(searchParams.get("need") || initialDraft.primaryNeed || "").trim();
  const entryPoint = String(searchParams.get("entry") || "start").trim();
  const initialStep = Math.max(0, Math.min(Number(initialDraft.stepIndex || 0), STEPS.length - 1));

  const [stepIndex, setStepIndex] = React.useState(initialStep);
  const [profile, setProfile] = React.useState({
    businessProfile: initialDraft.businessProfile || "",
    currentSystem: initialDraft.currentSystem || "",
    itemVolumeBand: initialDraft.itemVolumeBand || "",
    primaryNeed: quickNeed || "",
    businessName: initialDraft.businessName || "",
    locationText: initialDraft.locationText || "",
  });
  const [contact, setContact] = React.useState({
    name: initialDraft.contactName || user?.name || "",
    email: initialDraft.contactEmail || user?.email || "",
    whatsapp: initialDraft.whatsapp || "",
    password: "",
  });
  const [recommendation, setRecommendation] = React.useState(() => localRecommendation({ ...initialDraft, primaryNeed: quickNeed || initialDraft.primaryNeed || "" }));
  const [error, setError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const trackerRef = React.useRef(null);
  const initialTrackingRef = React.useRef(false);

  React.useEffect(() => {
    if (!user) return;
    setContact((current) => ({
      ...current,
      name: current.name || user.name || "",
      email: current.email || user.email || "",
    }));
  }, [user]);
  const stepKey = STEPS[stepIndex];
  const progressPct = Math.round((stepIndex / (STEPS.length - 1)) * 100);

  const eventPayload = React.useCallback((action, metadata = {}) => ({
    action,
    sessionId,
    visitorId,
    eventId: acquisitionEventId("ae"),
    metadata: {
      stepKey,
      stepIndex,
      totalSteps: STEPS.length,
      progressPct,
      entryPoint,
      audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
      ...acquisitionDeviceMetadata(),
      ...metadata,
    },
  }), [authLoading, entryPoint, progressPct, sessionId, stepIndex, stepKey, user, visitorId]);

  React.useEffect(() => {
    // Wait until /auth/me resolves so a logged-in owner testing this funnel is
    // classified as authenticated and excluded from public acquisition KPIs.
    if (authLoading || initialTrackingRef.current) return;
    initialTrackingRef.current = true;
    const campaign = acquisitionCampaign(searchParams);
    const audience = user ? "authenticated" : "anonymous";
    saveAcquisitionDraft({ sessionId, visitorId, referralCode, stepIndex, ...profile });
    acquisitionApi.startSession({ sessionId, visitorId, entryPoint: "funnel", referralCode, campaign, audience }).catch(() => null);
    acquisitionApi.track(eventPayload(initialStep > 0 ? "acquisition_funnel_resumed" : "acquisition_funnel_started", { direction: initialStep > 0 ? "resume" : "forward", audience })).catch(() => null);
    // Only once per mounted funnel session after auth resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  React.useEffect(() => {
    const tracker = {
      stepKey,
      startedAt: performance.now(),
      lastTickAt: performance.now(),
      lastInteractionAt: performance.now(),
      firstInteractionMs: null,
      activeMs: 0,
      idleMs: 0,
      hiddenMs: 0,
      flushed: false,
    };
    trackerRef.current = tracker;

    const interact = () => {
      const now = performance.now();
      tracker.lastInteractionAt = now;
      if (tracker.firstInteractionMs === null) tracker.firstInteractionMs = Math.max(0, Math.round(now - tracker.startedAt));
    };
    const tick = () => {
      const now = performance.now();
      const delta = Math.max(0, Math.min(2000, now - tracker.lastTickAt));
      tracker.lastTickAt = now;
      if (document.hidden) tracker.hiddenMs += delta;
      else if (now - tracker.lastInteractionAt > 12000) tracker.idleMs += delta;
      else tracker.activeMs += delta;
    };
    const interval = window.setInterval(tick, 750);
    window.addEventListener("pointerdown", interact, { passive: true });
    window.addEventListener("keydown", interact, { passive: true });
    window.addEventListener("input", interact, { passive: true });
    acquisitionApi.track(eventPayload("acquisition_step_viewed")).catch(() => null);

    return () => {
      tick();
      window.clearInterval(interval);
      window.removeEventListener("pointerdown", interact);
      window.removeEventListener("keydown", interact);
      window.removeEventListener("input", interact);
      if (!tracker.flushed) {
        tracker.flushed = true;
        acquisitionApi.trackKeepalive(eventPayload("acquisition_step_summary", {
          activeMs: Math.round(tracker.activeMs),
          idleMs: Math.round(tracker.idleMs),
          hiddenMs: Math.round(tracker.hiddenMs),
          firstInteractionMs: tracker.firstInteractionMs || 0,
        }));
      }
    };
  }, [eventPayload, stepKey]);

  React.useEffect(() => {
    const onPageHide = () => {
      const tracker = trackerRef.current;
      if (!tracker || tracker.flushed) return;
      tracker.flushed = true;
      acquisitionApi.trackKeepalive(eventPayload("acquisition_funnel_exited", { direction: "exit" }));
      acquisitionApi.trackKeepalive(eventPayload("acquisition_step_summary", {
        activeMs: Math.round(tracker.activeMs || 0), idleMs: Math.round(tracker.idleMs || 0), hiddenMs: Math.round(tracker.hiddenMs || 0), firstInteractionMs: tracker.firstInteractionMs || 0,
      }));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [eventPayload]);

  const persist = React.useCallback((nextProfile, nextStepIndex = stepIndex) => {
    const nextRecommendation = localRecommendation(nextProfile);
    setRecommendation(nextRecommendation);
    saveAcquisitionDraft({ sessionId, visitorId, referralCode, stepIndex: nextStepIndex, ...nextProfile, recommendation: nextRecommendation });
    acquisitionApi.updateSession(sessionId, {
      visitorId,
      audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
      stepKey: STEPS[nextStepIndex] || stepKey,
      stepIndex: nextStepIndex,
      completionPct: Math.round((nextStepIndex / (STEPS.length - 1)) * 100),
      profile: nextProfile,
    }).then((data) => {
      if (data?.recommendation) setRecommendation(data.recommendation);
    }).catch(() => null);
    return nextRecommendation;
  }, [authLoading, referralCode, sessionId, stepIndex, stepKey, user, visitorId]);

  const goNext = React.useCallback(() => {
    setError("");
    setStepIndex((current) => {
      const next = Math.min(STEPS.length - 1, current + 1);
      saveAcquisitionDraft({ stepIndex: next });
      return next;
    });
  }, []);

  const choose = (field, value) => {
    const nextProfile = { ...profile, [field]: value };
    setProfile(nextProfile);
    persist(nextProfile, stepIndex);
    acquisitionApi.track(eventPayload("acquisition_answer_selected", { optionKey: value })).catch(() => null);
    acquisitionApi.track(eventPayload("acquisition_step_completed", { optionKey: value })).catch(() => null);
    window.setTimeout(goNext, 140);
  };

  const goBack = () => {
    if (stepIndex <= 0) {
      navigate("/");
      return;
    }
    acquisitionApi.track(eventPayload("acquisition_step_back", { direction: "back" })).catch(() => null);
    setStepIndex((current) => {
      const next = Math.max(0, current - 1);
      saveAcquisitionDraft({ stepIndex: next });
      return next;
    });
  };

  const trackFormProgress = (formKey, fieldKey, values, requiredKeys, optionalKeys = []) => {
    const requiredCompleted = requiredKeys.filter((key) => String(values?.[key] || "").trim()).length;
    const optionalCompleted = optionalKeys.filter((key) => String(values?.[key] || "").trim()).length;
    acquisitionApi.track(eventPayload("acquisition_form_progress", {
      formKey,
      fieldKey,
      requiredCompleted,
      requiredTotal: requiredKeys.length,
      optionalCompleted,
    })).catch(() => null);
  };

  const continueBusinessIdentity = () => {
    const businessName = String(profile.businessName || "").trim();
    const locationText = String(profile.locationText || "").trim();
    if (businessName.length < 2) {
      setError("Poné el nombre del negocio para armar la cuenta.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "business_name" })).catch(() => null);
      return;
    }
    if (locationText.length < 2) {
      setError("Decinos de dónde es el negocio.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "location" })).catch(() => null);
      return;
    }
    persist({ ...profile, businessName, locationText }, stepIndex);
    acquisitionApi.track(eventPayload("acquisition_step_completed")).catch(() => null);
    goNext();
  };

  const createAccountAndProject = async () => {
    const name = String(contact.name || "").trim();
    const email = String(contact.email || "").trim().toLowerCase();
    if (!name || !email.includes("@")) {
      setError("Necesitamos tu nombre y un email válido.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "contact" })).catch(() => null);
      return;
    }
    if (!user && String(contact.password || "").length < 8) {
      setError("La contraseña necesita al menos 8 caracteres.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "password" })).catch(() => null);
      return;
    }

    try {
      setCreating(true);
      setError("");
      saveAcquisitionDraft({ contactName: name, contactEmail: email, whatsapp: contact.whatsapp });
      await acquisitionApi.updateSession(sessionId, {
        visitorId,
        audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
        stepKey: "contact",
        stepIndex,
        completionPct: 92,
        profile,
        contact: { name, email, whatsapp: contact.whatsapp },
      }).catch(() => null);
      await acquisitionApi.track(eventPayload("acquisition_contact_captured")).catch(() => null);

      if (!user) {
        await acquisitionApi.track(eventPayload("acquisition_registration_started")).catch(() => null);
        await register({
          name,
          email,
          password: contact.password,
          referralCode: referralCode || undefined,
          prospectSessionId: sessionId,
        });
      }

      const seed = recommendation?.businessGoals ? recommendation : localRecommendation(profile);
      let createdProject = null;
      try {
        createdProject = await createProject({
          name: profile.businessName,
          businessProfile: PROFILE_KEYS.has(profile.businessProfile) ? profile.businessProfile : "",
          businessGoals: seed.businessGoals || [],
          capabilities: seed.capabilities || {},
          prospectSessionId: sessionId,
        });
      } catch (projectError) {
        // Account success remains success: project provisioning is independently recoverable from dashboard.
        console.error("[ACQUISITION PROJECT CREATE]", projectError);
      }

      clearAcquisitionDraft();
      if (createdProject?.slug) {
        navigate(`/dashboard/proyectos/${createdProject.slug}/inicio`, { replace: true, state: { onboardingMessage: "Tu negocio ya tiene una ruta inicial. Empecemos por la primera tarea." } });
      } else {
        navigate("/dashboard", { replace: true, state: { onboardingMessage: "Tu cuenta está lista. Terminemos de crear tu negocio desde el panel." } });
      }
    } catch (err) {
      setError(err?.message || "No pudimos crear la cuenta. Tus respuestas quedaron guardadas para reintentar.");
    } finally {
      setCreating(false);
    }
  };

  React.useEffect(() => {
    if (stepKey === "plan") acquisitionApi.track(eventPayload("acquisition_plan_viewed")).catch(() => null);
    if (stepKey === "business_identity") acquisitionApi.track(eventPayload("acquisition_form_started", { formKey: "business_identity" })).catch(() => null);
    if (stepKey === "contact") {
      acquisitionApi.track(eventPayload("acquisition_contact_started")).catch(() => null);
      acquisitionApi.track(eventPayload("acquisition_form_started", { formKey: "contact" })).catch(() => null);
    }
  }, [eventPayload, stepKey]);

  const titleByStep = {
    intro: entryPoint === "what-is" ? "WebProfit aprende primero cómo trabajás." : "Armemos tu comienzo.",
    business: "¿Qué tipo de negocio tenés?",
    system: "¿Cómo te organizás hoy?",
    volume: "¿Cuántos productos o ítems manejás?",
    need: "¿Qué querés resolver primero?",
    plan: "Este sería tu primer camino.",
    business_identity: "Ahora pongámosle nombre.",
    contact: user ? "Listo. Creamos tu negocio." : "Listo. Creamos tu cuenta.",
  };

  return (
    <Box sx={{ height: "100dvh", overflow: "hidden", bgcolor: BRAND.dark, color: "#fff", display: "flex", flexDirection: "column", background: "radial-gradient(circle at 80% 10%, rgba(243,90,58,.18), transparent 28%), linear-gradient(180deg,#090E13,#0B1016)" }}>
      <Box sx={{ flex: "0 0 auto", px: { xs: 1.4, sm: 2.5 }, pt: { xs: 1.2, sm: 1.8 } }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <IconButton onClick={goBack} aria-label="Volver" sx={{ color: "rgba(255,255,255,.76)", border: "1px solid rgba(255,255,255,.08)" }}><ArrowBackRoundedIcon /></IconButton>
          <Box sx={{ flex: 1 }}>
            <LinearProgress variant="determinate" value={progressPct} sx={{ height: 7, borderRadius: 99, bgcolor: "rgba(255,255,255,.08)", "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: BRAND.orange } }} />
          </Box>
          <Typography sx={{ fontSize: ".76rem", fontWeight: 900, color: "rgba(255,255,255,.52)", minWidth: 42, textAlign: "right" }}>{stepIndex + 1}/{STEPS.length}</Typography>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, display: "grid", placeItems: "center", px: { xs: 1.4, sm: 2.5 }, py: { xs: 1.5, sm: 2.5 } }}>
        <Box sx={{ width: "100%", maxWidth: 760 }}>
          <Stack spacing={{ xs: 1.5, sm: 2 }} alignItems="center" textAlign="center" sx={{ mb: { xs: 1.8, sm: 2.5 } }}>
            <Chip icon={<AutoAwesomeRoundedIcon />} label={stepKey === "intro" ? "Diagnóstico WebProfit" : "Una decisión a la vez"} sx={{ bgcolor: "rgba(243,90,58,.10)", color: "#FFB19A", border: "1px solid rgba(243,90,58,.15)", fontWeight: 900, "& .MuiChip-icon": { color: BRAND.orange } }} />
            <Typography component="h1" sx={{ maxWidth: 700, fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.02, fontSize: { xs: "2rem", sm: "2.8rem", md: "3.35rem" } }}>{titleByStep[stepKey]}</Typography>
            {stepKey === "intro" ? <Typography sx={{ color: BRAND.muted, maxWidth: 580, lineHeight: 1.55, fontSize: { xs: ".92rem", sm: "1.02rem" } }}>No te mostramos veinte módulos. Entendemos tu negocio y te dejamos una primera tarea clara.</Typography> : null}
          </Stack>

          <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2 }, borderRadius: { xs: 3.5, sm: 4.5 }, bgcolor: "rgba(17,24,32,.92)", border: "1px solid rgba(255,255,255,.08)", color: "#fff", boxShadow: "0 28px 80px rgba(0,0,0,.32)", backdropFilter: "blur(18px)" }}>
            {stepKey === "intro" ? (
              <Stack spacing={1.1}>
                {["Te preguntamos lo mínimo.", "Te proponemos un orden.", "Tu panel arranca con una primera misión."].map((text) => <Stack key={text} direction="row" spacing={1} alignItems="center" sx={{ p: 1.2, borderRadius: 2.7, bgcolor: "rgba(255,255,255,.035)" }}><CheckCircleRoundedIcon sx={{ color: "#82DFB0", fontSize: 20 }} /><Typography sx={{ fontWeight: 800 }}>{text}</Typography></Stack>)}
                <Button onClick={() => { acquisitionApi.track(eventPayload("acquisition_step_completed")).catch(() => null); goNext(); }} variant="contained" size="large" endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: .6, minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>Empezar</Button>
              </Stack>
            ) : null}

            {stepKey === "business" ? <OptionGrid options={BUSINESS_OPTIONS} selected={profile.businessProfile} onSelect={(value) => choose("businessProfile", value)} /> : null}
            {stepKey === "system" ? <OptionGrid options={SYSTEM_OPTIONS} selected={profile.currentSystem} onSelect={(value) => choose("currentSystem", value)} /> : null}
            {stepKey === "volume" ? <OptionGrid options={VOLUME_OPTIONS} selected={profile.itemVolumeBand} onSelect={(value) => choose("itemVolumeBand", value)} /> : null}
            {stepKey === "need" ? <OptionGrid options={NEED_OPTIONS} selected={profile.primaryNeed} onSelect={(value) => choose("primaryNeed", value)} /> : null}

            {stepKey === "plan" ? (
              <Stack spacing={1.1}>
                {(recommendation?.firstTasks || []).map((task, index) => (
                  <Stack key={task} direction="row" spacing={1.2} alignItems="center" sx={{ p: { xs: 1.2, sm: 1.45 }, borderRadius: 3, bgcolor: index === 0 ? "rgba(243,90,58,.12)" : "rgba(255,255,255,.035)", border: index === 0 ? "1px solid rgba(243,90,58,.22)" : "1px solid rgba(255,255,255,.05)" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 99, display: "grid", placeItems: "center", bgcolor: index === 0 ? BRAND.orange : "rgba(255,255,255,.08)", color: "#fff", fontWeight: 950 }}>{index + 1}</Box>
                    <Box sx={{ textAlign: "left" }}><Typography sx={{ fontWeight: 950 }}>{TASK_LABELS[task] || task}</Typography>{index === 0 ? <Typography sx={{ color: BRAND.muted, fontSize: ".78rem" }}>Esta sería la primera tarea que va a brillar dentro del panel.</Typography> : null}</Box>
                  </Stack>
                ))}
                <Button onClick={() => { acquisitionApi.track(eventPayload("acquisition_step_completed")).catch(() => null); goNext(); }} variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 50, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>Me sirve</Button>
              </Stack>
            ) : null}

            {stepKey === "business_identity" ? (
              <Stack spacing={1.15}>
                <TextField autoFocus fullWidth label="Nombre del negocio" value={profile.businessName} onChange={(event) => setProfile((current) => ({ ...current, businessName: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "businessName", profile, ["businessName", "locationText"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth label="Ciudad / zona" placeholder="Ej: San Rafael, Mendoza" value={profile.locationText} onChange={(event) => setProfile((current) => ({ ...current, locationText: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "locationText", profile, ["businessName", "locationText"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                {error ? <Alert severity="warning">{error}</Alert> : null}
                <Button onClick={continueBusinessIdentity} variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 50, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>Continuar</Button>
              </Stack>
            ) : null}

            {stepKey === "contact" ? (
              <Stack spacing={1.05}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.1, borderRadius: 2.7, bgcolor: "rgba(255,255,255,.035)" }}><StorefrontRoundedIcon sx={{ color: "#FFB19A" }} /><Box sx={{ textAlign: "left" }}><Typography sx={{ fontWeight: 950 }}>{profile.businessName || "Tu negocio"}</Typography><Typography sx={{ color: BRAND.muted, fontSize: ".76rem" }}>{profile.locationText || ""}</Typography></Box></Stack>
                <TextField fullWidth label="Tu nombre" value={contact.name} onChange={(event) => setContact((current) => ({ ...current, name: event.target.value }))} onBlur={() => trackFormProgress("contact", "name", contact, user ? ["name", "email"] : ["name", "email", "password"], ["whatsapp"])} disabled={creating || Boolean(user)} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth type="email" label="Email" value={contact.email} onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))} onBlur={() => trackFormProgress("contact", "email", contact, user ? ["name", "email"] : ["name", "email", "password"], ["whatsapp"])} disabled={creating || Boolean(user)} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth label="WhatsApp (opcional)" value={contact.whatsapp} onChange={(event) => setContact((current) => ({ ...current, whatsapp: event.target.value }))} onBlur={() => trackFormProgress("contact", "whatsapp", contact, user ? ["name", "email"] : ["name", "email", "password"], ["whatsapp"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                {!user ? <TextField fullWidth type="password" label="Contraseña" helperText="Mínimo 8 caracteres" value={contact.password} onChange={(event) => setContact((current) => ({ ...current, password: event.target.value }))} onBlur={() => trackFormProgress("contact", "password", contact, ["name", "email", "password"], ["whatsapp"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 }, "& .MuiFormHelperText-root": { color: "rgba(255,255,255,.48)" } }} /> : null}
                <Typography sx={{ color: "rgba(255,255,255,.42)", fontSize: ".7rem", lineHeight: 1.35, textAlign: "left" }}>Usamos estos datos para crear tu cuenta y conservar la configuración que acabás de elegir. La telemetría de uso no guarda lo que escribís en los campos.</Typography>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button onClick={createAccountAndProject} disabled={creating} variant="contained" startIcon={creating ? <WebProfitSpinner size={17} /> : undefined} endIcon={!creating ? <ArrowForwardRoundedIcon /> : undefined} sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>{creating ? "Preparando tu negocio..." : user ? "Crear mi negocio" : "Crear cuenta y entrar"}</Button>
              </Stack>
            ) : null}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
